import { cn } from '@/lib/utils/cn';

/**
 * ReportOutline — the sections of a report draft down the side (prototype
 * `.outline`; built for Health & Readiness in T-0245, shared in T-0272 so
 * the Vendor intelligence report draws the same one). Each section is a
 * button that scrolls the document to it, with a dot that says whether the
 * text is still the AI's draft or a consultant edited it. Sticky at the top
 * of the scrolling column from the `lg` breakpoint.
 */
export interface ReportOutlineItem {
  id: string;
  title: string;
  /** A consultant edited or rewrote the section's text. */
  edited: boolean;
}

export interface ReportOutlineProps {
  items: readonly ReportOutlineItem[];
  onJump: (id: string) => void;
  /** The landmark's accessible name. */
  label?: string;
  className?: string;
}

export const OUTLINE_AI_LABEL = 'AI draft';
export const OUTLINE_EDITED_LABEL = 'Edited by a consultant';

export function ReportOutline({
  items,
  onJump,
  label = 'Report sections',
  className,
}: ReportOutlineProps) {
  return (
    <nav
      aria-label={label}
      className={cn(
        'flex flex-col gap-px rounded-card border border-ink-100 bg-paper-0 p-1.5 lg:sticky lg:top-0',
        className,
      )}
    >
      {items.map((s) => {
        const title = s.edited ? OUTLINE_EDITED_LABEL : OUTLINE_AI_LABEL;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onJump(s.id)}
            className="flex items-center gap-2 rounded-[6px] p-2 text-left text-body-sm text-ink-900 hover:bg-paper-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-amber-600"
          >
            <span className="min-w-0 flex-1">{s.title}</span>
            <span
              aria-label={title}
              title={title}
              className={cn(
                'h-2 w-2 flex-none rounded-full',
                s.edited ? 'bg-ink-700' : 'bg-ai-surface-border',
              )}
            />
          </button>
        );
      })}
      <div className="mt-1 flex flex-wrap gap-x-3.5 gap-y-1 border-t border-ink-100 px-2 py-2.5 text-meta text-ink-700">
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden className="h-2.5 w-2.5 rounded-full bg-ai-surface-border" />
          {OUTLINE_AI_LABEL}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden className="h-2.5 w-2.5 rounded-full bg-ink-700" />
          Edited
        </span>
      </div>
    </nav>
  );
}
